import './contact-card.scss';

import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '@sola/ark-core/atoms/badge/badge.tsx';
import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.tsx';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@sola/ark-core/molecules/dropdown-menu/dropdown-menu.tsx';
import {
  ItemCard,
  ItemCardDivider,
  ItemCardFooter,
  ItemCardHeader,
  ItemCardSection,
} from '@sola/ark-core/molecules/item-card/item-card.tsx';
import { ArkIcon } from '@sola/ark-icons/lib/ark-icon.tsx';
import type { GraphQLSupporterContact } from '@sola/schema/dotcom/supporters/schema.generated.ts';

import { ContactPreferencesSheet } from '../../organisms/contact-preferences-sheet/contact-preferences-sheet.tsx';
import { SettingsModalContact } from '../../organisms/settings-modal-contact/settings-modal-contact.tsx';
import { useComponentContactCard } from './contact-card.logic.ts';

type ContactDropdownProps = {
  contact: GraphQLSupporterContact;
};

const PLACEHOLDER = '—-';

const ContactDropdown: React.FC<ContactDropdownProps> = ({ contact }) => {
  const { t } = useTranslation('my-settings');
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger>
          <button
            type="button"
            aria-label={t('my-settings:contactCard.moreOptions')}
            className="ContactCard__kebab flex items-center justify-center -m-snug-xl p-snug-xl rounded-md border-none bg-transparent cursor-pointer label-neutral-subtle"
          >
            <ArkIcon iconName="dots-horizontal" size="1.25rem" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="ContactCard__menu">
          <DropdownMenuItem
            onSelect={() => setIsOpen(true)}
            className="ContactCard__menu-item"
          >
            <LinkStyle
              purpose="neutral"
              size="md"
              fullWidth
              noUnderline
              noPadding
            >
              <button type="button">
                <div className="ContactCard__menu-item-content">
                  <ArkIcon iconName="edit-02" size="1rem" />
                  {t('my-settings:contactCard.editAction')}
                </div>
              </button>
            </LinkStyle>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <SettingsModalContact
        contactId={contact.id}
        open={isOpen}
        onOpenChange={setIsOpen}
      />
    </>
  );
};

export type ContactCardProps = {
  contact: GraphQLSupporterContact;
};

export const ContactCard: React.FC<ContactCardProps> = ({ contact }) => {
  const { t } = useTranslation(['my-settings']);
  const {
    firstName,
    lastName,
    email,
    phone,
    isPrimary = false,
    preferences,
  } = contact;

  const { emailPart, textPart, isDoNotContact, isDoNotEmail, isDoNotText } =
    useComponentContactCard(preferences);

  const summaryParts: string[] = [];
  if (!isDoNotEmail) {
    summaryParts.push(emailPart);
  }
  if (!isDoNotText) {
    summaryParts.push(textPart);
  }
  const summaryLine = summaryParts.join(', ');

  const headerTitleBadge = isPrimary && (
    <Badge
      label={t('my-settings:contactCard.primaryBadge')}
      color="brand"
      size="sm"
    />
  );

  return (
    <ItemCard>
      <ItemCardSection>
        <ItemCardHeader
          title={
            firstName ? `${firstName} ${lastName}` : (lastName ?? PLACEHOLDER)
          }
          titleBadge={headerTitleBadge}
          action={<ContactDropdown contact={contact} />}
          isTitleLarge
        />

        <div className="flex flex-col md:flex-row md:items-center gap-comfy-xs md:gap-comfy-sm min-w-0">
          <div className="flex items-center gap-snug-xl min-w-0 flex-1">
            <ArkIcon
              iconName="mail-01"
              size="1rem"
              className="label-neutral-subtle"
            />
            <span className="label-neutral-mild label-moderate label-lg">
              <span className="sr-only">
                {t('my-settings:contactCard.emailLabel')}
              </span>
              {email ?? PLACEHOLDER}
            </span>
          </div>
          <div className="flex items-center gap-snug-xl min-w-0 flex-1">
            <ArkIcon
              iconName="phone"
              size="1rem"
              className="label-neutral-subtle"
            />
            <span className="label-neutral-mild label-moderate label-lg">
              <span className="sr-only">
                {t('my-settings:contactCard.phoneLabel')}
              </span>
              {phone ?? PLACEHOLDER}
            </span>
          </div>
        </div>
      </ItemCardSection>

      <ItemCardDivider />

      <ItemCardFooter justify="between">
        <div className="w-full flex flex-col md:flex-row md:items-center gap-comfy-md md:gap-comfy-xs">
          <div className="flex flex-row md:items-center gap-snug-md min-w-0 flex-1">
            <div className="flex items-center gap-snug-xl shrink-0">
              <ArkIcon
                iconName="bell-03"
                size="1rem"
                className="label-neutral-subtle"
              />
              <span className="label-neutral-potent label-potent label-md">
                {t('my-settings:contactCard.preferencesLabel')}
              </span>
            </div>
            {isDoNotContact ? (
              <Badge
                label={t('my-settings:contactCard.noContactBadge')}
                color="negative"
                size="sm"
              />
            ) : (
              <div className="flex items-center gap-snug-md min-w-0">
                {summaryLine && (
                  <span className="label-neutral-mild label-moderate label-md">
                    {summaryLine}
                  </span>
                )}
                {isDoNotEmail && (
                  <Badge
                    label={t('my-settings:contactCard.noEmailBadge')}
                    color="negative"
                    size="sm"
                  />
                )}
                {isDoNotText && (
                  <Badge
                    label={t('my-settings:contactCard.noTextBadge')}
                    color="negative"
                    size="sm"
                  />
                )}
              </div>
            )}
          </div>

          <div className="pl-comfy-lg md:pl-0 md:shrink-0">
            <ContactPreferencesSheet contactId={contact.id} />
          </div>
        </div>
      </ItemCardFooter>
    </ItemCard>
  );
};
